import React, { useState, useEffect, useCallback } from 'react';
import {
  getPolicyTemplates,
  getRequiredPolicies,
  getMyOrganizationPolicies,
  getAvailableTemplates,
  createOrganizationPolicyV2,
  uploadOrganizationPolicyFile,
  uploadTemplateTrainingVideo,
  deleteTemplateTrainingVideo,
  reorderTemplateTrainingVideos,
  uploadOrgPolicyTrainingVideo,
  deleteOrgPolicyTrainingVideo,
  reorderOrgPolicyTrainingVideos,
  updateOrganizationPolicy,
  deleteOrganizationPolicy,
  adoptPolicyTemplate,
  getPolicyComplianceSummary,
  seedPolicyTemplates,
  markPolicyEquivalent,
  syncPoliciesFromRecommendations,
  downloadPolicyPdf,
  getPolicyChangeHistory,
  createPolicyTemplate,
  updatePolicyTemplate,
  deletePolicyTemplate
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { 
  FileText,
  Plus,
  AlertTriangle,
  CheckCircle,
  Building2,
  BookOpen,
  Shield,
  ChevronRight,
  Calendar,
  Download,
  Copy,
  Eye,
  Clock,
  AlertCircle,
  Loader2,
  Library,
  Target,
  TrendingUp,
  ExternalLink,
  Edit,
  Trash2,
  FileDown,
  History,
  RefreshCw,
  GraduationCap,
  PlayCircle,
  GripVertical,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row used inside the "Currently attached videos" list in edit dialogs
function SortableVideoItem({ video, onPlay, onRemove, removing, isPrimary }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: video.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between gap-2 text-xs rounded px-2 py-1 hover:bg-secondary/60 bg-card border border-border/50"
      data-testid={`video-item-${video.id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-0.5"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <PlayCircle className="w-3.5 h-3.5 text-fuchsia-400 flex-shrink-0" />
        <span className="truncate" title={video.title || video.filename || video.url}>
          {video.title || video.filename || video.url}
        </span>
        {isPrimary && (
          <span className="text-[9px] uppercase tracking-wide text-fuchsia-300 bg-fuchsia-500/10 px-1.5 py-0.5 rounded">Primary</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPlay}
          className="text-fuchsia-400 hover:text-fuchsia-300 text-[11px] px-1"
        >
          Play
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          className="text-red-400 hover:text-red-300 text-[11px] px-1 disabled:opacity-50"
        >
          {removing ? '...' : 'Remove'}
        </button>
      </div>
    </div>
  );
}

const POLICY_CATEGORIES = ['Safety', 'HR', 'Operations', 'Compliance', 'Emergency', 'Training', 
  'Risk Management Policy', 'Background Checks', 'Health & Safety', 'Facilities', 'Policies', 'Other'];

const ORG_TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'youth_sports', label: 'Youth Sports' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'professional', label: 'Professional' }
];

// Returns ORG_TYPE_FILTERS but hides any filter (except 'all') that has no policies in the provided template list
const getVisibleOrgTypeFilters = (templates) => {
  const typesPresent = new Set();
  (templates || []).forEach(t => {
    const types = t.applicable_org_types || [];
    if (types.length === 0) {
      // Universal - applies to all known types
      typesPresent.add('youth_sports');
      typesPresent.add('fitness');
    } else {
      types.forEach(tt => typesPresent.add(tt));
    }
  });
  return ORG_TYPE_FILTERS.filter(f => f.value === 'all' || typesPresent.has(f.value));
};

// Converts a YouTube/Vimeo URL to an embed URL. Returns null if not embeddable.
const toEmbedUrl = (raw) => {
  if (!raw) return null;
  const url = raw.trim();
  // YouTube watch URL
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{6,})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Already an embed
  if (url.includes('/embed/') || url.includes('player.vimeo.com')) return url;
  // Direct MP4
  if (/\.mp4(\?|$)/i.test(url)) return url;
  return url; // fallback - let iframe try
};

export default function Policies() {
  const { isOrgAdmin, isSuperAdmin, user } = useAuth();
  const { isPlatformMode, isOrganizationMode, selectedOrg, switchToOrganizationMode } = useAdminMode();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('required');
  const [orgTypeFilter, setOrgTypeFilter] = useState('all');
  
  // Data states
  const [requiredPolicies, setRequiredPolicies] = useState([]);
  const [myPolicies, setMyPolicies] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [compliance, setCompliance] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAdoptDialog, setShowAdoptDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
  const [showEditTemplateDialog, setShowEditTemplateDialog] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyHistory, setPolicyHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  
  // Create policy mode: 'paste' (manual content) or 'upload' (file upload)
  const [createMode, setCreateMode] = useState('paste');
  const [uploadFile, setUploadFile] = useState(null);
  const [validateAgainstTemplateId, setValidateAgainstTemplateId] = useState('');
  
  // Training video upload state (templates AND org policies)
  // Note: arrays support uploading MULTIPLE videos per save
  const [templateVideoFiles, setTemplateVideoFiles] = useState([]);
  const [orgPolicyVideoFiles, setOrgPolicyVideoFiles] = useState([]);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoUploading, setVideoUploading] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [removingVideoId, setRemovingVideoId] = useState(null);
  
  // Quick video viewer dialog (lets users watch the attached video without opening full preview)
  // Supports multi-video playlist: pass `videos` array (or fall back to single url)
  const [videoViewer, setVideoViewer] = useState({ open: false, videos: [], activeIndex: 0, title: '' });
  const openVideoViewer = (urlOrVideos, title) => {
    let videos = [];
    if (Array.isArray(urlOrVideos)) {
      videos = urlOrVideos;
    } else if (urlOrVideos) {
      videos = [{ id: 'single', url: urlOrVideos, title }];
    }
    if (videos.length === 0) return;
    setVideoViewer({ open: true, videos, activeIndex: 0, title });
  };
  
  // Form state
  const [newPolicy, setNewPolicy] = useState({
    title: '',
    category: '',
    content: '',
    effective_date: new Date().toISOString().split('T')[0],
    description: '',
    training_video_url: ''
  });
  
  const [editForm, setEditForm] = useState({
    title: '',
    category: '',
    content: '',
    effective_date: '',
    description: '',
    training_video_url: ''
  });
  
  const [adoptForm, setAdoptForm] = useState({
    template_id: '',
    custom_content: '',
    effective_date: new Date().toISOString().split('T')[0]
  });

  // PH Library template form state (Super Admin)
  const [templateForm, setTemplateForm] = useState({
    title: '',
    category: '',
    content: '',
    description: '',
    linked_risk_categories: [],
    linked_assessment_questions: [],
    applicable_org_types: [],
    training_video_url: ''
  });

  // Determine if we have org context
  const hasOrgContext = isSuperAdmin() 
    ? (isOrganizationMode && selectedOrg) 
    : !!user?.organization_id;

  const fetchData = useCallback(async () => {
    // Only fetch if we have organization context (or Super Admin for library management)
    if (isSuperAdmin() && !hasOrgContext) {
      // Super Admin without org context can still manage PH Library
      setLoading(true);
      try {
        const params = orgTypeFilter !== 'all' ? { org_type: orgTypeFilter } : {};
        const templatesRes = await getPolicyTemplates(params);
        setAllTemplates(templatesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      // First, sync policies from assessment recommendations
      const orgId = selectedOrg?.id || user?.organization_id;
      if (orgId) {
        try {
          await syncPoliciesFromRecommendations(orgId);
        } catch (syncError) {
          // Ignore sync errors - recommendations may not exist
          console.log('No recommendations to sync or already synced');
        }
      }
      
      const templateParams = orgTypeFilter !== 'all' ? { org_type: orgTypeFilter } : {};
      const [requiredRes, myRes, libraryRes, complianceRes, templatesRes] = await Promise.all([
        getRequiredPolicies(),
        getMyOrganizationPolicies(),
        getAvailableTemplates(),
        getPolicyComplianceSummary(),
        isSuperAdmin() ? getPolicyTemplates(templateParams) : Promise.resolve({ data: [] })
      ]);
      
      setRequiredPolicies(requiredRes.data);
      setMyPolicies(myRes.data);
      setAvailableTemplates(libraryRes.data.templates || []);
      setCompliance(complianceRes.data);
      if (isSuperAdmin()) {
        setAllTemplates(templatesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      // Try to seed templates if none exist
      if (isSuperAdmin()) {
        try {
          await seedPolicyTemplates();
          toast.info('Policy templates initialized');
        } catch (e) {
          // Ignore
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, hasOrgContext, selectedOrg, user?.organization_id, orgTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    if (!newPolicy.title || !newPolicy.category) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (createMode === 'paste' && !newPolicy.content) {
      toast.error('Please paste the policy content');
      return;
    }
    if (createMode === 'upload' && !uploadFile) {
      toast.error('Please choose a DOCX or PDF file to upload');
      return;
    }
    
    setSubmitting(true);
    try {
      if (createMode === 'upload') {
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('title', newPolicy.title);
        fd.append('category', newPolicy.category);
        fd.append('description', newPolicy.description || '');
        fd.append('effective_date', newPolicy.effective_date || '');
        fd.append('training_video_url', newPolicy.training_video_url || '');
        if (validateAgainstTemplateId) {
          fd.append('source_template_id', validateAgainstTemplateId);
          fd.append('validate_with_ai', 'true');
        }
        const res = await uploadOrganizationPolicyFile(fd);
        const v = res?.data?.ai_validation;
        if (v) {
          if (v.satisfies) {
            toast.success(`Policy uploaded — AI says coverage satisfies the template (${v.coverage_score}%)`);
          } else if (v.error) {
            toast.warning('Policy uploaded, but AI validation failed. You can retry later.');
          } else {
            toast.warning(`Policy uploaded — coverage ${v.coverage_score}%. Missing: ${(v.missing_components || []).slice(0, 3).join(', ')}${(v.missing_components || []).length > 3 ? '…' : ''}`);
          }
        } else {
          toast.success('Policy uploaded successfully');
        }
      } else {
        await createOrganizationPolicyV2(newPolicy);
        toast.success('Policy created successfully');
      }
      setShowCreateDialog(false);
      setNewPolicy({
        title: '',
        category: '',
        content: '',
        effective_date: new Date().toISOString().split('T')[0],
        description: '',
        training_video_url: ''
      });
      setUploadFile(null);
      setValidateAgainstTemplateId('');
      setCreateMode('paste');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdoptTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSubmitting(true);
    try {
      await adoptPolicyTemplate({
        template_id: selectedTemplate.id,
        custom_content: adoptForm.custom_content || null,
        effective_date: adoptForm.effective_date
      });
      toast.success(`"${selectedTemplate.title}" adopted successfully`);
      setShowAdoptDialog(false);
      setSelectedTemplate(null);
      setAdoptForm({
        template_id: '',
        custom_content: '',
        effective_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adopt template');
    } finally {
      setSubmitting(false);
    }
  };

  const openAdoptDialog = (template) => {
    setSelectedTemplate(template);
    setAdoptForm({
      template_id: template.id,
      custom_content: template.content,
      effective_date: new Date().toISOString().split('T')[0]
    });
    // Show disclaimer first
    setDisclaimerAccepted(false);
    setShowDisclaimerDialog(true);
  };

  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimerDialog(false);
    setShowAdoptDialog(true);
  };

  const openEditDialog = (policy) => {
    setSelectedPolicy(policy);
    setEditForm({
      title: policy.title,
      category: policy.category,
      content: policy.content,
      effective_date: policy.effective_date || '',
      description: policy.description || '',
      training_video_url: policy.training_video_url || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdatePolicy = async () => {
    if (!selectedPolicy) return;
    
    setSubmitting(true);
    try {
      await updateOrganizationPolicy(selectedPolicy.id, editForm);

      // If user picked MP4 file(s), upload them against this policy
      if (orgPolicyVideoFiles.length > 0) {
        setVideoUploading(true);
        for (let i = 0; i < orgPolicyVideoFiles.length; i++) {
          setCurrentUploadIndex(i + 1);
          try {
            const fd = new FormData();
            fd.append('file', orgPolicyVideoFiles[i]);
            await uploadOrgPolicyTrainingVideo(selectedPolicy.id, fd, (e) => {
              if (e.total) setVideoUploadProgress(Math.round((e.loaded / e.total) * 100));
            });
          } catch (err) {
            toast.error(err.response?.data?.detail || `Failed uploading ${orgPolicyVideoFiles[i].name}`);
          }
        }
        setVideoUploading(false);
        setVideoUploadProgress(0);
        setCurrentUploadIndex(0);
        toast.success(`Uploaded ${orgPolicyVideoFiles.length} video${orgPolicyVideoFiles.length > 1 ? 's' : ''}`);
      }

      toast.success('Policy updated successfully');
      setShowEditDialog(false);
      setSelectedPolicy(null);
      setOrgPolicyVideoFiles([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePolicy = async (policyId, policyTitle) => {
    try {
      await deleteOrganizationPolicy(policyId);
      toast.success(`"${policyTitle}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete policy');
    }
  };

  const handleDownloadPdf = async (policy) => {
    setDownloadingPdf(policy.id);
    try {
      const response = await downloadPolicyPdf(policy.id);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${policy.title.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleViewHistory = async (policy) => {
    setSelectedPolicy(policy);
    setLoadingHistory(true);
    setShowHistoryDialog(true);
    try {
      const response = await getPolicyChangeHistory(policy.id);
      setPolicyHistory(response.data);
    } catch (error) {
      toast.error('Failed to load change history');
      setPolicyHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // PH Library template CRUD handlers (Super Admin only)
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.title || !templateForm.category || !templateForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const created = await createPolicyTemplate(templateForm);
      const newTemplateId = created?.data?.id;

      // If user chose MP4 file(s), upload them sequentially against the new template
      if (templateVideoFiles.length > 0 && newTemplateId) {
        setVideoUploading(true);
        for (let i = 0; i < templateVideoFiles.length; i++) {
          setCurrentUploadIndex(i + 1);
          try {
            const fd = new FormData();
            fd.append('file', templateVideoFiles[i]);
            await uploadTemplateTrainingVideo(newTemplateId, fd, (e) => {
              if (e.total) setVideoUploadProgress(Math.round((e.loaded / e.total) * 100));
            });
          } catch (err) {
            toast.error(err.response?.data?.detail || `Failed uploading ${templateVideoFiles[i].name}`);
          }
        }
        setVideoUploading(false);
        setVideoUploadProgress(0);
        setCurrentUploadIndex(0);
        toast.success(`Uploaded ${templateVideoFiles.length} video${templateVideoFiles.length > 1 ? 's' : ''}`);
      }

      toast.success('PH Library template created successfully');
      setShowCreateTemplateDialog(false);
      setTemplateForm({
        title: '',
        category: '',
        content: '',
        description: '',
        linked_risk_categories: [],
        linked_assessment_questions: [],
        applicable_org_types: [],
        training_video_url: ''
      });
      setTemplateVideoFiles([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditTemplateDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      title: template.title,
      category: template.category,
      content: template.content,
      description: template.description || '',
      linked_risk_categories: template.linked_risk_categories || [],
      applicable_org_types: template.applicable_org_types || [],
      linked_assessment_questions: template.linked_assessment_questions || [],
      training_video_url: template.training_video_url || ''
    });
    setShowEditTemplateDialog(true);
  };

  // Remove a single video from a template (immediate API call + state refresh)
  const handleRemoveTemplateVideo = async (videoId) => {
    if (!selectedTemplate) return;
    setRemovingVideoId(videoId);
    try {
      const res = await deleteTemplateTrainingVideo(selectedTemplate.id, videoId);
      const remaining = res.data?.training_videos || [];
      setSelectedTemplate({ ...selectedTemplate, training_videos: remaining, training_video_url: remaining[0]?.url || null });
      toast.success('Video removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove video');
    } finally {
      setRemovingVideoId(null);
    }
  };

  // Reorder videos on a template (drag-and-drop)
  const handleReorderTemplateVideos = async (newOrder) => {
    if (!selectedTemplate) return;
    // Optimistic update
    const prev = selectedTemplate.training_videos || [];
    setSelectedTemplate({ ...selectedTemplate, training_videos: newOrder, training_video_url: newOrder[0]?.url || null });
    try {
      await reorderTemplateTrainingVideos(selectedTemplate.id, newOrder.map(v => v.id));
      toast.success('Video order saved');
      fetchData();
    } catch (err) {
      // Rollback on error
      setSelectedTemplate({ ...selectedTemplate, training_videos: prev, training_video_url: prev[0]?.url || null });
      toast.error(err.response?.data?.detail || 'Failed to reorder videos');
    }
  };

  const handleRemoveOrgPolicyVideo = async (videoId) => {
    if (!selectedPolicy) return;
    setRemovingVideoId(videoId);
    try {
      const res = await deleteOrgPolicyTrainingVideo(selectedPolicy.id, videoId);
      const remaining = res.data?.training_videos || [];
      setSelectedPolicy({ ...selectedPolicy, training_videos: remaining, training_video_url: remaining[0]?.url || null });
      toast.success('Video removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove video');
    } finally {
      setRemovingVideoId(null);
    }
  };

  const handleReorderOrgPolicyVideos = async (newOrder) => {
    if (!selectedPolicy) return;
    const prev = selectedPolicy.training_videos || [];
    setSelectedPolicy({ ...selectedPolicy, training_videos: newOrder, training_video_url: newOrder[0]?.url || null });
    try {
      await reorderOrgPolicyTrainingVideos(selectedPolicy.id, newOrder.map(v => v.id));
      toast.success('Video order saved');
      fetchData();
    } catch (err) {
      setSelectedPolicy({ ...selectedPolicy, training_videos: prev, training_video_url: prev[0]?.url || null });
      toast.error(err.response?.data?.detail || 'Failed to reorder videos');
    }
  };

  // dnd-kit sensors - reuse across both lists
  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    try {
      await updatePolicyTemplate(selectedTemplate.id, templateForm);

      // If user picked new MP4 file(s), upload them against the template
      if (templateVideoFiles.length > 0) {
        setVideoUploading(true);
        for (let i = 0; i < templateVideoFiles.length; i++) {
          setCurrentUploadIndex(i + 1);
          try {
            const fd = new FormData();
            fd.append('file', templateVideoFiles[i]);
            await uploadTemplateTrainingVideo(selectedTemplate.id, fd, (e) => {
              if (e.total) setVideoUploadProgress(Math.round((e.loaded / e.total) * 100));
            });
          } catch (err) {
            toast.error(err.response?.data?.detail || `Failed uploading ${templateVideoFiles[i].name}`);
          }
        }
        setVideoUploading(false);
        setVideoUploadProgress(0);
        setCurrentUploadIndex(0);
        toast.success(`Uploaded ${templateVideoFiles.length} video${templateVideoFiles.length > 1 ? 's' : ''}`);
      }

      toast.success('Template updated successfully');
      setShowEditTemplateDialog(false);
      setSelectedTemplate(null);
      setTemplateVideoFiles([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateTitle) => {
    try {
      await deletePolicyTemplate(templateId);
      toast.success(`"${templateTitle}" deleted from PH Library`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const getRiskBadgeColor = (level) => {
    switch (level) {
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'adopted') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'not_adopted') return <Clock className="w-4 h-4 text-yellow-500" />;
    return <AlertCircle className="w-4 h-4 text-gray-500" />;
  };

  // Super Admin without org context - show PH Library management
  if (isSuperAdmin() && !hasOrgContext) {
    return (
      <div data-testid="policies-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              PH Library Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage Players Health policy templates available to all organizations
            </p>
          </div>
          <Dialog open={showCreateTemplateDialog} onOpenChange={setShowCreateTemplateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-template-btn" className="btn-primary-glow">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                  Create PH Library Template
                </DialogTitle>
                <DialogDescription>
                  Add a new policy template to the Players Health library
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Template Title *</Label>
                    <Input
                      value={templateForm.title}
                      onChange={(e) => setTemplateForm({...templateForm, title: e.target.value})}
                      placeholder="e.g., Concussion Protocol Policy"
                      required
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select 
                      value={templateForm.category} 
                      onValueChange={(v) => setTemplateForm({...templateForm, category: v})}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {POLICY_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                      placeholder="Brief description of this policy template"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Applicable Organization Types</Label>
                    <div className="flex flex-wrap gap-2">
                      {ORG_TYPE_FILTERS.filter(t => t.value !== 'all').map(type => (
                        <Button
                          key={type.value}
                          type="button"
                          variant={templateForm.applicable_org_types.includes(type.value) ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            const newTypes = templateForm.applicable_org_types.includes(type.value)
                              ? templateForm.applicable_org_types.filter(t => t !== type.value)
                              : [...templateForm.applicable_org_types, type.value];
                            setTemplateForm({...templateForm, applicable_org_types: newTypes});
                          }}
                        >
                          {type.label}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Leave empty for universal policies applicable to all organization types</p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Training Video(s) <span className="text-muted-foreground text-xs font-normal">(optional — attach multiple)</span></Label>
                    <Input
                      value={templateForm.training_video_url}
                      onChange={(e) => setTemplateForm({...templateForm, training_video_url: e.target.value})}
                      placeholder="Paste a YouTube / Vimeo / .mp4 URL (sets the primary video)"
                      className="bg-secondary/50"
                      data-testid="template-training-video-input"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">or upload one or more MP4s:</span>
                      <Input
                        type="file"
                        multiple
                        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
                        onChange={(e) => setTemplateVideoFiles(Array.from(e.target.files || []))}
                        className="bg-secondary/50 max-w-xs"
                        data-testid="template-training-video-file"
                      />
                    </div>
                    {templateVideoFiles.length > 0 && (
                      <div className="space-y-1 text-xs text-green-600 dark:text-green-400">
                        <p className="font-medium">{templateVideoFiles.length} file{templateVideoFiles.length > 1 ? 's' : ''} ready to upload after save:</p>
                        <ul className="pl-4 list-disc">
                          {templateVideoFiles.map((f) => (
                            <li key={`tpl-vid-create-${f.name}-${f.size}-${f.lastModified}`}>{f.name} ({Math.round(f.size / (1024*1024))} MB)</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {videoUploading && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Uploading video {currentUploadIndex} of {templateVideoFiles.length}... {videoUploadProgress}%</p>
                        <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-primary transition-all" style={{ width: `${videoUploadProgress}%` }} />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Paste a link OR upload multiple MP4s (max 200 MB each). All attached videos cascade to organizations that adopt this template.
                    </p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Template Content *</Label>
                    <Textarea
                      value={templateForm.content}
                      onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                      placeholder="Enter policy template content..."
                      rows={12}
                      required
                      className="bg-secondary/50 font-mono text-sm"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary-glow" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Template
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Alert className="bg-blue-500/10 border-blue-500/30 flex-1">
            <Library className="w-4 h-4 text-blue-400" />
            <AlertDescription className="text-blue-200">
              You are managing the PH Library. To manage organization-specific policies, switch to Organization mode and select an organization.
            </AlertDescription>
          </Alert>
        </div>

        {/* Org Type Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {ORG_TYPE_FILTERS.map(type => (
            <Button
              key={type.value}
              variant={orgTypeFilter === type.value ? "default" : "outline"}
              size="sm"
              onClick={() => setOrgTypeFilter(type.value)}
              data-testid={`filter-${type.value}`}
            >
              {type.label}
            </Button>
          ))}
        </div>

        {/* PH Library Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : allTemplates.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {allTemplates.map((template) => (
              <Card 
                key={template.id}
                className="bg-card border-border card-hover"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{template.title}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                        {template.adoption_count > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {template.adoption_count} adoptions
                          </Badge>
                        )}
                        {template.training_video_url && (
                          <Badge className="text-[10px] bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 flex items-center gap-1" data-testid={`tpl-video-badge-${template.id}`}>
                            <PlayCircle className="w-3 h-3" />
                            {(template.training_videos?.length || 1) > 1 ? `${template.training_videos.length} Videos` : 'Video'}
                          </Badge>
                        )}
                        {/* Org Type Badges */}
                        {template.applicable_org_types?.length > 0 ? (
                          template.applicable_org_types.map(type => (
                            <Badge key={type} className={cn(
                              "text-[10px]",
                              type === 'youth_sports' ? "bg-green-500/20 text-green-400 border-green-500/30" :
                              type === 'fitness' ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                              type === 'professional' ? "bg-orange-500/20 text-orange-400 border-orange-500/30" :
                              "bg-gray-500/20 text-gray-400 border-gray-500/30"
                            )}>
                              {type === 'youth_sports' ? 'Youth Sports' : 
                               type === 'fitness' ? 'Fitness' : 
                               type === 'professional' ? 'Professional' : type}
                            </Badge>
                          ))
                        ) : (
                          <Badge className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                            Universal
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedPolicy(template);
                            setShowPreviewDialog(true);
                          }}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                        {template.training_video_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                            onClick={() => openVideoViewer(template.training_videos?.length ? template.training_videos : template.training_video_url, template.title)}
                            data-testid={`watch-video-${template.id}`}
                          >
                            <PlayCircle className="w-3 h-3 mr-1" />
                            {(template.training_videos?.length || 1) > 1 ? `Watch (${template.training_videos.length})` : 'Watch Video'}
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => openEditTemplateDialog(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-template-${template.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Template</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{template.title}" from the PH Library? 
                                This will not affect organizations that have already adopted this template.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => handleDeleteTemplate(template.id, template.title)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Library className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No Templates in PH Library</p>
              <p className="text-sm mb-4">Create your first policy template to get started.</p>
              <Button onClick={() => setShowCreateTemplateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Edit Template Dialog */}
        <Dialog open={showEditTemplateDialog} onOpenChange={setShowEditTemplateDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                Edit PH Library Template
              </DialogTitle>
              <DialogDescription>
                Update this policy template in the Players Health library
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Template Title *</Label>
                  <Input
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm({...templateForm, title: e.target.value})}
                    placeholder="e.g., Concussion Protocol Policy"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={templateForm.category} 
                    onValueChange={(v) => setTemplateForm({...templateForm, category: v})}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {POLICY_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                    placeholder="Brief description of this policy template"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Applicable Organization Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {ORG_TYPE_FILTERS.filter(t => t.value !== 'all').map(type => (
                      <Button
                        key={type.value}
                        type="button"
                        variant={templateForm.applicable_org_types.includes(type.value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const newTypes = templateForm.applicable_org_types.includes(type.value)
                            ? templateForm.applicable_org_types.filter(t => t !== type.value)
                            : [...templateForm.applicable_org_types, type.value];
                          setTemplateForm({...templateForm, applicable_org_types: newTypes});
                        }}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Leave empty for universal policies applicable to all organization types</p>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Training Video(s) <span className="text-muted-foreground text-xs font-normal">(optional — attach multiple)</span></Label>

                  {/* Currently attached videos */}
                  {selectedTemplate?.training_videos?.length > 0 && (
                    <div className="rounded-md border border-border p-2 space-y-1 bg-secondary/30">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                        Currently attached ({selectedTemplate.training_videos.length}) — drag to reorder
                      </p>
                      <DndContext
                        sensors={sortableSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => {
                          const { active, over } = event;
                          if (over && active.id !== over.id) {
                            const items = selectedTemplate.training_videos;
                            const oldIndex = items.findIndex(v => v.id === active.id);
                            const newIndex = items.findIndex(v => v.id === over.id);
                            const newOrder = arrayMove(items, oldIndex, newIndex);
                            handleReorderTemplateVideos(newOrder);
                          }
                        }}
                      >
                        <SortableContext
                          items={selectedTemplate.training_videos.map(v => v.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-1">
                            {selectedTemplate.training_videos.map((v, idx) => (
                              <SortableVideoItem
                                key={v.id}
                                video={v}
                                isPrimary={idx === 0}
                                onPlay={() => openVideoViewer(v.url, v.title || selectedTemplate.title)}
                                onRemove={() => handleRemoveTemplateVideo(v.id)}
                                removing={removingVideoId === v.id}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}

                  <Input
                    value={templateForm.training_video_url}
                    onChange={(e) => setTemplateForm({...templateForm, training_video_url: e.target.value})}
                    placeholder="Paste a YouTube / Vimeo / .mp4 URL (sets the primary video)"
                    className="bg-secondary/50"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">or upload one or more MP4s:</span>
                    <Input
                      type="file"
                      multiple
                      accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
                      onChange={(e) => setTemplateVideoFiles(Array.from(e.target.files || []))}
                      className="bg-secondary/50 max-w-xs"
                    />
                  </div>
                  {templateVideoFiles.length > 0 && (
                    <div className="space-y-1 text-xs text-green-600 dark:text-green-400">
                      <p className="font-medium">{templateVideoFiles.length} new file{templateVideoFiles.length > 1 ? 's' : ''} queued (added on Save):</p>
                      <ul className="pl-4 list-disc">
                        {templateVideoFiles.map((f) => (
                          <li key={`tpl-vid-create-${f.name}-${f.size}-${f.lastModified}`}>{f.name} ({Math.round(f.size / (1024*1024))} MB)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {videoUploading && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Uploading {currentUploadIndex} of {templateVideoFiles.length}... {videoUploadProgress}%</p>
                      <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${videoUploadProgress}%` }} />
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    New MP4 files are appended to the current list. Existing videos remain unless you click Remove.
                  </p>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Template Content *</Label>
                  <Textarea
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                    placeholder="Enter policy template content..."
                    rows={12}
                    className="bg-secondary/50 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowEditTemplateDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary-glow"
                onClick={handleUpdateTemplate}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Video Viewer Dialog (quick play, multi-video playlist) */}
        <Dialog open={videoViewer.open} onOpenChange={(o) => setVideoViewer({ ...videoViewer, open: o })}>
          <DialogContent className="bg-card border-border max-w-4xl">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-fuchsia-400" />
                Training Video{videoViewer.videos.length > 1 ? 's' : ''} — {videoViewer.title}
              </DialogTitle>
              {videoViewer.videos.length > 1 && (
                <DialogDescription>
                  {videoViewer.activeIndex + 1} of {videoViewer.videos.length} — {videoViewer.videos[videoViewer.activeIndex]?.title || 'Untitled'}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-3 mt-2">
              {/* Active player */}
              <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
                {videoViewer.videos[videoViewer.activeIndex] && (() => {
                  const v = videoViewer.videos[videoViewer.activeIndex];
                  const url = v.url;
                  const isFile = /\.mp4(\?|$)|\.webm(\?|$)|\.mov(\?|$)/i.test(url) || url.includes('/uploads/training_videos/');
                  return isFile ? (
                    <video key={v.id} src={url} controls autoPlay className="w-full h-full" />
                  ) : (
                    <iframe
                      key={v.id}
                      src={toEmbedUrl(url)}
                      title={v.title || 'Training video'}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  );
                })()}
              </div>

              {/* Playlist sidebar (only when 2+ videos) */}
              {videoViewer.videos.length > 1 && (
                <div className="space-y-1 max-h-[60vh] overflow-y-auto border border-border rounded-lg p-2 bg-secondary/30">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 mb-1">Playlist</p>
                  {videoViewer.videos.map((v, idx) => (
                    <button
                      key={v.id || idx}
                      type="button"
                      onClick={() => setVideoViewer({ ...videoViewer, activeIndex: idx })}
                      className={`w-full text-left px-2 py-2 rounded text-xs transition-colors ${
                        idx === videoViewer.activeIndex
                          ? 'bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/30'
                          : 'hover:bg-secondary/60 text-muted-foreground'
                      }`}
                      data-testid={`playlist-video-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <PlayCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{v.title || v.filename || `Video ${idx + 1}`}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Policy Preview Dialog (reused) */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedPolicy && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{selectedPolicy.category}</Badge>
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      <BookOpen className="w-3 h-3 mr-1" />
                      PH Template
                    </Badge>
                  </div>
                  <DialogTitle className="text-xl mt-2">
                    {selectedPolicy.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4 prose prose-invert max-w-none">
                  <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                    {selectedPolicy.content}
                  </p>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="policies-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Safety Policy Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Safety protocols, compliance requirements, and organizational safety policies
          </p>
        </div>
        {isOrgAdmin() && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-policy-btn" className="btn-primary-glow">
                <Plus className="w-4 h-4 mr-2" />
                Upload Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                  Upload Organization Policy
                </DialogTitle>
                <DialogDescription>
                  Upload a policy your organization has created
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePolicy} className="space-y-4 mt-4">
                {/* Mode Toggle */}
                <div className="flex gap-2 p-1 bg-secondary/50 rounded-md">
                  <button
                    type="button"
                    onClick={() => setCreateMode('upload')}
                    className={cn(
                      "flex-1 px-3 py-2 text-sm rounded transition-colors",
                      createMode === 'upload' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                    )}
                    data-testid="mode-upload"
                  >
                    Upload Document (.docx / .pdf)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateMode('paste')}
                    className={cn(
                      "flex-1 px-3 py-2 text-sm rounded transition-colors",
                      createMode === 'paste' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                    )}
                    data-testid="mode-paste"
                  >
                    Paste Content
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Policy Title *</Label>
                    <Input
                      value={newPolicy.title}
                      onChange={(e) => setNewPolicy({...newPolicy, title: e.target.value})}
                      placeholder="Emergency Response Policy"
                      required
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select 
                      value={newPolicy.category} 
                      onValueChange={(v) => setNewPolicy({...newPolicy, category: v})}
                    >
                      <SelectTrigger className="bg-secondary/50">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {POLICY_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Effective Date</Label>
                    <Input
                      type="date"
                      value={newPolicy.effective_date}
                      onChange={(e) => setNewPolicy({...newPolicy, effective_date: e.target.value})}
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newPolicy.description}
                      onChange={(e) => setNewPolicy({...newPolicy, description: e.target.value})}
                      placeholder="Brief description of the policy"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Training Video URL <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                    <Input
                      value={newPolicy.training_video_url}
                      onChange={(e) => setNewPolicy({...newPolicy, training_video_url: e.target.value})}
                      placeholder="e.g., https://www.youtube.com/embed/VIDEO_ID or https://player.vimeo.com/video/VIDEO_ID"
                      className="bg-secondary/50"
                      data-testid="policy-training-video-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Embed a training video directly inside this policy. Paste a YouTube, Vimeo, or direct MP4 URL — your staff will see it when viewing the policy.
                    </p>
                  </div>
                  {createMode === 'upload' ? (
                    <div className="col-span-2 space-y-2">
                      <Label>Policy Document *</Label>
                      <Input
                        type="file"
                        accept=".docx,.pdf"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="bg-secondary/50"
                        required
                        data-testid="policy-file-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Upload a Word (.docx) or PDF document. The system will extract its text as the policy content.
                      </p>
                      {uploadFile && (
                        <p className="text-xs text-green-600 dark:text-green-400">
                          Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                        </p>
                      )}

                      {/* AI Coverage Validation */}
                      <div className="mt-4 p-3 rounded-md border border-purple-500/30 bg-purple-500/5 space-y-2">
                        <Label className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                          <Sparkles className="w-4 h-4" />
                          AI Coverage Validation (optional)
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Select a required Players Health policy this document is intended to satisfy. We'll use AI to check
                          whether your document covers the required components.
                        </p>
                        <Select
                          value={validateAgainstTemplateId || 'none'}
                          onValueChange={(v) => setValidateAgainstTemplateId(v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="bg-secondary/50" data-testid="validate-against-template-select">
                            <SelectValue placeholder="Select a required policy to validate against (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Don't validate, just upload —</SelectItem>
                            {(requiredPolicies || [])
                              .filter(p => p.source_template_id || p.adoption_status === 'not_adopted')
                              .map(p => (
                                <SelectItem key={p.source_template_id || p.id} value={p.source_template_id || p.id}>
                                  {p.title} {p.category ? `(${p.category})` : ''}
                                </SelectItem>
                              ))}
                            {(availableTemplates || []).map(t => (
                              <SelectItem key={`tpl-${t.id}`} value={t.id}>
                                {t.title} {t.category ? `(${t.category})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    <div className="col-span-2 space-y-2">
                      <Label>Policy Content *</Label>
                      <Textarea
                        value={newPolicy.content}
                        onChange={(e) => setNewPolicy({...newPolicy, content: e.target.value})}
                        placeholder="Enter policy details..."
                        rows={10}
                        required
                        className="bg-secondary/50"
                      />
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full btn-primary-glow" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {createMode === 'upload' ? 'Upload Policy' : 'Create Policy'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Compliance Summary Widget */}
      {compliance && (
        <Card className="bg-gradient-to-br from-card to-card/50 border-border">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-16 h-16 rounded-xl flex items-center justify-center",
                  getRiskBadgeColor(compliance.risk_impact)
                )}>
                  <Shield className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Policy Compliance Score</p>
                  <p className="text-3xl font-bold">{compliance.compliance_score}%</p>
                </div>
              </div>
              
              <div className="flex-1 max-w-md">
                <div className="flex justify-between text-sm mb-2">
                  <span>{compliance.completed} of {compliance.total_required} Required Policies Complete</span>
                  <span className={cn(
                    "font-medium",
                    compliance.risk_impact === 'low' ? 'text-green-400' :
                    compliance.risk_impact === 'medium' ? 'text-yellow-400' :
                    compliance.risk_impact === 'high' ? 'text-orange-400' : 'text-red-400'
                  )}>
                    {compliance.risk_impact.toUpperCase()} RISK IMPACT
                  </span>
                </div>
                <Progress value={compliance.compliance_score} className="h-3" />
              </div>
              
              <div className="flex gap-4 text-center">
                <div className="px-4">
                  <p className="text-2xl font-bold text-green-400">{compliance.completed}</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </div>
                <div className="border-l border-border" />
                <div className="px-4">
                  <p className="text-2xl font-bold text-orange-400">{compliance.outstanding}</p>
                  <p className="text-xs text-muted-foreground">Outstanding</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Three-Segment Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-2xl bg-secondary/50">
          <TabsTrigger value="required" className="flex items-center gap-2" data-testid="required-policies-tab">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Required</span>
            {requiredPolicies.length > 0 && (
              <Badge variant="destructive" className="ml-1">{requiredPolicies.filter(p => p.adoption_status !== 'adopted').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="my-policies" className="flex items-center gap-2" data-testid="my-policies-tab">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">My Policies</span>
            <Badge variant="secondary" className="ml-1">{myPolicies.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="library" className="flex items-center gap-2" data-testid="library-tab">
            <Library className="w-4 h-4" />
            <span className="hidden sm:inline">PH Library</span>
            <Badge variant="outline" className="ml-1">{availableTemplates.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Required Policies Tab */}
        <TabsContent value="required" className="space-y-4">
          <Alert className="bg-orange-500/10 border-orange-500/30">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <AlertDescription className="text-orange-200">
              These policies are required based on your Risk Assessment results. Complete them to improve your compliance score.
            </AlertDescription>
          </Alert>
          
          {requiredPolicies.length > 0 ? (
            <div className="grid gap-4">
              {requiredPolicies.map((policy) => (
                <Card 
                  key={policy.id}
                  className={cn(
                    "bg-card border-border card-hover cursor-pointer",
                    policy.adoption_status === 'adopted' ? 'border-green-500/30' : 'border-orange-500/30'
                  )}
                  onClick={() => {
                    setSelectedPolicy(policy);
                    setShowPreviewDialog(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                          policy.adoption_status === 'adopted' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-orange-500/20 text-orange-400'
                        )}>
                          {policy.adoption_status === 'adopted' ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <AlertTriangle className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{policy.title}</h3>
                            <Badge variant="destructive" className="text-[10px]">REQUIRED</Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {policy.category}
                            </Badge>
                            {policy.linked_risk_category && (
                              <Badge variant="secondary" className="text-xs">
                                <Target className="w-3 h-3 mr-1" />
                                {policy.linked_risk_category}
                              </Badge>
                            )}
                          </div>
                          {policy.required_reason && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {policy.required_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {policy.adoption_status === 'adopted' ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            className="btn-primary-glow"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAdoptDialog(policy);
                            }}
                          >
                            Adopt Policy
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mb-4 text-green-500" />
                <p className="text-lg font-medium">All Required Policies Complete!</p>
                <p className="text-sm">Your organization has no outstanding policy requirements.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* My Organization Policies Tab */}
        <TabsContent value="my-policies" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Policies your organization has created or adopted
            </p>
          </div>
          
          {myPolicies.length > 0 ? (
            <div className="grid gap-4">
              {myPolicies.map((policy) => (
                <Card 
                  key={policy.id}
                  className="bg-card border-border card-hover"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-4 flex-1 cursor-pointer"
                        onClick={() => {
                          setSelectedPolicy(policy);
                          setShowPreviewDialog(true);
                        }}
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{policy.title}</h3>
                            {policy.policy_origin === 'ph_template' && (
                              <Badge variant="secondary" className="text-[10px]">
                                <BookOpen className="w-3 h-3 mr-1" />
                                PH Template
                              </Badge>
                            )}
                            {policy.policy_origin === 'organization_uploaded' && (
                              <Badge variant="outline" className="text-[10px]">
                                <Building2 className="w-3 h-3 mr-1" />
                                Uploaded
                              </Badge>
                            )}
                            {policy.training_video_url && (
                              <Badge className="text-[10px] bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 flex items-center gap-1">
                                <PlayCircle className="w-3 h-3" />
                                {(policy.training_videos?.length || 1) > 1 ? `${policy.training_videos.length} Videos` : 'Video'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {policy.category}
                            </Badge>
                            {policy.effective_date && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Effective: {new Date(policy.effective_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {/* Watch Video - if attached */}
                        {policy.training_video_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              openVideoViewer(policy.training_videos?.length ? policy.training_videos : policy.training_video_url, policy.title);
                            }}
                            data-testid={`watch-video-policy-${policy.id}`}
                          >
                            <PlayCircle className="w-4 h-4" />
                            <span className="ml-1 hidden sm:inline">
                              {(policy.training_videos?.length || 1) > 1 ? `Watch (${policy.training_videos.length})` : 'Watch'}
                            </span>
                          </Button>
                        )}
                        {/* PDF Download - only for adopted policies */}
                        {policy.adoption_status === 'adopted' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadPdf(policy);
                            }}
                            disabled={downloadingPdf === policy.id}
                            data-testid={`download-pdf-${policy.id}`}
                          >
                            {downloadingPdf === policy.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileDown className="w-4 h-4" />
                            )}
                            <span className="ml-1 hidden sm:inline">PDF</span>
                          </Button>
                        )}
                        
                        {/* Update Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(policy);
                          }}
                          data-testid={`update-policy-${policy.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span className="ml-1 hidden sm:inline">Update</span>
                        </Button>
                        
                        {/* History Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewHistory(policy);
                          }}
                          data-testid={`history-policy-${policy.id}`}
                        >
                          <History className="w-4 h-4" />
                          <span className="ml-1 hidden sm:inline">History</span>
                        </Button>
                        
                        {/* Delete Button */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`delete-policy-${policy.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Policy</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{policy.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => handleDeletePolicy(policy.id, policy.title)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <FileText className="w-12 h-12 mb-4 opacity-50" />
                <p>No organization policies yet</p>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Policy
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('library')}>
                    <Library className="w-4 h-4 mr-2" />
                    Browse Templates
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Players Health Library Tab */}
        <TabsContent value="library" className="space-y-4">
          <div className="flex items-center justify-between">
            <Alert className="bg-blue-500/10 border-blue-500/30 flex-1">
              <BookOpen className="w-4 h-4 text-blue-400" />
              <AlertDescription className="text-blue-200">
                These are optional best-practice templates from Players Health. Adopt them to establish robust safety policies, or use them as references for your own policies.
              </AlertDescription>
            </Alert>
            {isSuperAdmin() && (
              <Dialog open={showCreateTemplateDialog} onOpenChange={setShowCreateTemplateDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-template-btn-inline" className="btn-primary-glow ml-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                      Create PH Library Template
                    </DialogTitle>
                    <DialogDescription>
                      Add a new policy template to the Players Health library
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label>Template Title *</Label>
                        <Input
                          value={templateForm.title}
                          onChange={(e) => setTemplateForm({...templateForm, title: e.target.value})}
                          placeholder="e.g., Concussion Protocol Policy"
                          required
                          className="bg-secondary/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select 
                          value={templateForm.category} 
                          onValueChange={(v) => setTemplateForm({...templateForm, category: v})}
                        >
                          <SelectTrigger className="bg-secondary/50">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {POLICY_CATEGORIES.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={templateForm.description}
                          onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                          placeholder="Brief description of this policy template"
                          className="bg-secondary/50"
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Applicable Organization Types</Label>
                        <div className="flex flex-wrap gap-2">
                          {ORG_TYPE_FILTERS.filter(t => t.value !== 'all').map(type => (
                            <Button
                              key={type.value}
                              type="button"
                              variant={templateForm.applicable_org_types.includes(type.value) ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const newTypes = templateForm.applicable_org_types.includes(type.value)
                                  ? templateForm.applicable_org_types.filter(t => t !== type.value)
                                  : [...templateForm.applicable_org_types, type.value];
                                setTemplateForm({...templateForm, applicable_org_types: newTypes});
                              }}
                            >
                              {type.label}
                            </Button>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">Leave empty for universal policies</p>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Template Content *</Label>
                        <Textarea
                          value={templateForm.content}
                          onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                          placeholder="Enter policy template content..."
                          rows={12}
                          required
                          className="bg-secondary/50 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-primary-glow" disabled={submitting}>
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Template
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          
          {/* Org Type Filter Tabs */}
          <div className="flex gap-2 flex-wrap">
            {getVisibleOrgTypeFilters(availableTemplates).map(type => (
              <Button
                key={type.value}
                variant={orgTypeFilter === type.value ? "default" : "outline"}
                size="sm"
                onClick={() => setOrgTypeFilter(type.value)}
                data-testid={`lib-filter-${type.value}`}
              >
                {type.label}
              </Button>
            ))}
          </div>
          
          {availableTemplates.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {availableTemplates
                .filter(template => {
                  if (orgTypeFilter === 'all') return true;
                  // Show templates that match the filter OR have no types (universal)
                  return !template.applicable_org_types?.length || 
                         template.applicable_org_types.includes(orgTypeFilter);
                })
                .map((template) => (
                <Card 
                  key={template.id}
                  className="bg-card border-border card-hover"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate">{template.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {template.category}
                          </Badge>
                          {template.adoption_count > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {template.adoption_count} adoptions
                            </span>
                          )}
                          {template.training_video_url && (
                            <Badge className="text-[10px] bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 flex items-center gap-1">
                              <PlayCircle className="w-3 h-3" />
                              {(template.training_videos?.length || 1) > 1 ? `${template.training_videos.length} Videos` : 'Video'}
                            </Badge>
                          )}
                          {/* Org Type Badges */}
                          {template.applicable_org_types?.length > 0 ? (
                            template.applicable_org_types.map(type => (
                              <Badge key={type} className={cn(
                                "text-[10px]",
                                type === 'youth_sports' ? "bg-green-500/20 text-green-400 border-green-500/30" :
                                type === 'fitness' ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                                type === 'professional' ? "bg-orange-500/20 text-orange-400 border-orange-500/30" :
                                "bg-gray-500/20 text-gray-400 border-gray-500/30"
                              )}>
                                {type === 'youth_sports' ? 'Youth' : 
                                 type === 'fitness' ? 'Fitness' : 
                                 type === 'professional' ? 'Pro' : type}
                              </Badge>
                            ))
                          ) : (
                            <Badge className="text-[10px] bg-blue-500/20 text-blue-400 border-blue-500/30">
                              Universal
                            </Badge>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                            {template.description}
                          </p>
                        )}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedPolicy(template);
                              setShowPreviewDialog(true);
                            }}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                          {template.training_video_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                              onClick={() => openVideoViewer(template.training_videos?.length ? template.training_videos : template.training_video_url, template.title)}
                            >
                              <PlayCircle className="w-3 h-3 mr-1" />
                              {(template.training_videos?.length || 1) > 1 ? `Watch (${template.training_videos.length})` : 'Watch'}
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            className="btn-primary-glow"
                            onClick={() => openAdoptDialog(template)}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Adopt
                          </Button>
                          {isSuperAdmin() && (
                            <>
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => openEditTemplateDialog(template)}
                                data-testid={`edit-template-lib-${template.id}`}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    data-testid={`delete-template-lib-${template.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-card border-border">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{template.title}" from the PH Library? 
                                      This will not affect organizations that have already adopted this template.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-red-500 hover:bg-red-600"
                                      onClick={() => handleDeleteTemplate(template.id, template.title)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mb-4 text-green-500" />
                <p className="text-lg font-medium">All Templates Adopted!</p>
                <p className="text-sm">You've adopted all available Players Health templates.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Liability Disclaimer Dialog */}
      <Dialog open={showDisclaimerDialog} onOpenChange={setShowDisclaimerDialog}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  Important Legal Notice
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Please read carefully before adopting this policy
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <h4 className="font-semibold text-amber-300 mb-2">Disclaimer & Liability Waiver</h4>
              <div className="text-sm text-muted-foreground space-y-3">
                <p>
                  The policy templates provided by Players Health ("PH Templates") are offered as 
                  <strong className="text-foreground"> general guidance resources only</strong> and are not intended 
                  to constitute legal advice or to replace consultation with qualified legal counsel.
                </p>
                <p>
                  By adopting this policy template, you acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>
                    <strong className="text-foreground">No Legal Advice:</strong> These templates do not constitute legal advice 
                    and should not be relied upon as such. Players Health is not a law firm and does not provide legal services.
                  </li>
                  <li>
                    <strong className="text-foreground">Consult Legal Counsel:</strong> You are strongly encouraged to consult 
                    with qualified legal counsel in your jurisdiction before adopting, implementing, or enforcing any policy 
                    based on these templates.
                  </li>
                  <li>
                    <strong className="text-foreground">No Warranties:</strong> Players Health makes no representations or 
                    warranties regarding the accuracy, completeness, or suitability of these templates for your specific 
                    organization, jurisdiction, or circumstances.
                  </li>
                  <li>
                    <strong className="text-foreground">Limitation of Liability:</strong> Players Health, its affiliates, 
                    officers, directors, employees, and agents shall not be liable for any claims, damages, losses, or 
                    expenses arising from the use of these policy templates.
                  </li>
                  <li>
                    <strong className="text-foreground">Your Responsibility:</strong> Your organization assumes full 
                    responsibility for reviewing, customizing, and ensuring compliance with all applicable laws and 
                    regulations in your jurisdiction.
                  </li>
                </ul>
              </div>
            </div>
            
            <Alert className="bg-blue-500/10 border-blue-500/20">
              <Shield className="w-4 h-4 text-blue-400" />
              <AlertDescription className="text-blue-200">
                <strong>Recommendation:</strong> Have your legal team review and approve all policies before 
                implementation to ensure compliance with local, state, and federal regulations.
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDisclaimerDialog(false);
                setSelectedTemplate(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              className="btn-primary-glow"
              onClick={handleDisclaimerAccept}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              I Understand & Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adopt Policy Dialog */}
      <Dialog open={showAdoptDialog} onOpenChange={setShowAdoptDialog}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
              Adopt Policy Template
            </DialogTitle>
            <DialogDescription>
              Review and customize this template before adopting it as your organization's policy
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{selectedTemplate.category}</Badge>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  <BookOpen className="w-3 h-3 mr-1" />
                  PH Template
                </Badge>
              </div>
              
              <h3 className="text-xl font-semibold">{selectedTemplate.title}</h3>
              
              {selectedTemplate.training_video_url && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold uppercase tracking-wide">Training Video</span>
                  </div>
                  <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
                    {/\.mp4(\?|$)/i.test(selectedTemplate.training_video_url) ? (
                      <video src={selectedTemplate.training_video_url} controls className="w-full h-full" />
                    ) : (
                      <iframe
                        src={toEmbedUrl(selectedTemplate.training_video_url)}
                        title="Training video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={adoptForm.effective_date}
                  onChange={(e) => setAdoptForm({...adoptForm, effective_date: e.target.value})}
                  className="bg-secondary/50 max-w-xs"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Policy Content (customize if needed)</Label>
                <Textarea
                  value={adoptForm.custom_content}
                  onChange={(e) => setAdoptForm({...adoptForm, custom_content: e.target.value})}
                  rows={15}
                  className="bg-secondary/50 font-mono text-sm"
                />
              </div>
              
              <Alert className="bg-green-500/10 border-green-500/30">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <AlertDescription className="text-green-200">
                  Adopting this template will create an organization-level policy. You can customize it now or edit it later.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAdoptDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary-glow"
              onClick={handleAdoptTemplate}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Adopt as Organization Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Viewer Dialog (quick play, multi-video playlist) */}
      <Dialog open={videoViewer.open} onOpenChange={(o) => setVideoViewer({ ...videoViewer, open: o })}>
        <DialogContent className="bg-card border-border max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-fuchsia-400" />
              Training Video{videoViewer.videos.length > 1 ? 's' : ''} — {videoViewer.title}
            </DialogTitle>
            {videoViewer.videos.length > 1 && (
              <DialogDescription>
                {videoViewer.activeIndex + 1} of {videoViewer.videos.length} — {videoViewer.videos[videoViewer.activeIndex]?.title || 'Untitled'}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-3 mt-2">
            <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
              {videoViewer.videos[videoViewer.activeIndex] && (() => {
                const v = videoViewer.videos[videoViewer.activeIndex];
                const url = v.url;
                const isFile = /\.mp4(\?|$)|\.webm(\?|$)|\.mov(\?|$)/i.test(url) || url.includes('/uploads/training_videos/');
                return isFile ? (
                  <video key={v.id} src={url} controls autoPlay className="w-full h-full" />
                ) : (
                  <iframe
                    key={v.id}
                    src={toEmbedUrl(url)}
                    title={v.title || 'Training video'}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                );
              })()}
            </div>
            {videoViewer.videos.length > 1 && (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto border border-border rounded-lg p-2 bg-secondary/30">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 mb-1">Playlist</p>
                {videoViewer.videos.map((v, idx) => (
                  <button
                    key={v.id || idx}
                    type="button"
                    onClick={() => setVideoViewer({ ...videoViewer, activeIndex: idx })}
                    className={`w-full text-left px-2 py-2 rounded text-xs transition-colors ${
                      idx === videoViewer.activeIndex
                        ? 'bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/30'
                        : 'hover:bg-secondary/60 text-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <PlayCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{v.title || v.filename || `Video ${idx + 1}`}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedPolicy && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{selectedPolicy.category}</Badge>
                  {selectedPolicy.policy_origin === 'ph_template' && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      <BookOpen className="w-3 h-3 mr-1" />
                      PH Template
                    </Badge>
                  )}
                  {selectedPolicy.policy_origin === 'organization_uploaded' && (
                    <Badge variant="secondary">
                      <Building2 className="w-3 h-3 mr-1" />
                      Organization Uploaded
                    </Badge>
                  )}
                  {selectedPolicy.required_status && selectedPolicy.required_status !== 'not_required' && (
                    <Badge variant="destructive">REQUIRED</Badge>
                  )}
                  {selectedPolicy.adoption_status === 'adopted' && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Adopted
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-xl mt-2">
                  {selectedPolicy.title}
                </DialogTitle>
                {selectedPolicy.effective_date && (
                  <p className="text-sm text-muted-foreground">
                    Effective: {new Date(selectedPolicy.effective_date).toLocaleDateString()}
                  </p>
                )}
              </DialogHeader>
              {selectedPolicy.training_video_url && (
                <div className="mt-4 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold uppercase tracking-wide">Training Video</span>
                  </div>
                  <div className="aspect-video w-full rounded-lg overflow-hidden border border-border bg-black">
                    {/\.mp4(\?|$)/i.test(selectedPolicy.training_video_url) ? (
                      <video src={selectedPolicy.training_video_url} controls className="w-full h-full" />
                    ) : (
                      <iframe
                        src={toEmbedUrl(selectedPolicy.training_video_url)}
                        title="Training video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>
              )}
              <div className="mt-4 prose prose-invert max-w-none">
                <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                  {selectedPolicy.content}
                </p>
              </div>
              {selectedPolicy.required_reason && (
                <Alert className="mt-4 bg-orange-500/10 border-orange-500/30">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <AlertDescription className="text-orange-200">
                    <strong>Why Required:</strong> {selectedPolicy.required_reason}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Policy Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
              Update Policy
            </DialogTitle>
            <DialogDescription>
              Update the policy details and content. Changes will be tracked in the history.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Policy Title *</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  placeholder="Policy title"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={editForm.category} 
                  onValueChange={(v) => setEditForm({...editForm, category: v})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {POLICY_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={editForm.effective_date}
                  onChange={(e) => setEditForm({...editForm, effective_date: e.target.value})}
                  className="bg-secondary/50"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Brief description of the policy"
                  className="bg-secondary/50"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Training Video(s) <span className="text-muted-foreground text-xs font-normal">(optional — attach multiple)</span></Label>

                {/* Currently attached videos */}
                {selectedPolicy?.training_videos?.length > 0 && (
                  <div className="rounded-md border border-border p-2 space-y-1 bg-secondary/30">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      Currently attached ({selectedPolicy.training_videos.length}) — drag to reorder
                    </p>
                    <DndContext
                      sensors={sortableSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => {
                        const { active, over } = event;
                        if (over && active.id !== over.id) {
                          const items = selectedPolicy.training_videos;
                          const oldIndex = items.findIndex(v => v.id === active.id);
                          const newIndex = items.findIndex(v => v.id === over.id);
                          const newOrder = arrayMove(items, oldIndex, newIndex);
                          handleReorderOrgPolicyVideos(newOrder);
                        }
                      }}
                    >
                      <SortableContext
                        items={selectedPolicy.training_videos.map(v => v.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-1">
                          {selectedPolicy.training_videos.map((v, idx) => (
                            <SortableVideoItem
                              key={v.id}
                              video={v}
                              isPrimary={idx === 0}
                              onPlay={() => openVideoViewer(v.url, v.title || selectedPolicy.title)}
                              onRemove={() => handleRemoveOrgPolicyVideo(v.id)}
                              removing={removingVideoId === v.id}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                <Input
                  value={editForm.training_video_url}
                  onChange={(e) => setEditForm({...editForm, training_video_url: e.target.value})}
                  placeholder="Paste a YouTube / Vimeo / .mp4 URL (sets the primary video)"
                  className="bg-secondary/50"
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">or upload one or more MP4s:</span>
                  <Input
                    type="file"
                    multiple
                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
                    onChange={(e) => setOrgPolicyVideoFiles(Array.from(e.target.files || []))}
                    className="bg-secondary/50 max-w-xs"
                  />
                </div>
                {orgPolicyVideoFiles.length > 0 && (
                  <div className="space-y-1 text-xs text-green-600 dark:text-green-400">
                    <p className="font-medium">{orgPolicyVideoFiles.length} new file{orgPolicyVideoFiles.length > 1 ? 's' : ''} queued (added on Save):</p>
                    <ul className="pl-4 list-disc">
                      {orgPolicyVideoFiles.map((f) => (
                        <li key={`org-vid-${f.name}-${f.size}-${f.lastModified}`}>{f.name} ({Math.round(f.size / (1024*1024))} MB)</li>
                      ))}
                    </ul>
                  </div>
                )}
                {videoUploading && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Uploading {currentUploadIndex} of {orgPolicyVideoFiles.length}... {videoUploadProgress}%</p>
                    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${videoUploadProgress}%` }} />
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  New MP4 files are appended to the current list. Existing videos remain unless you click Remove.
                </p>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Policy Content *</Label>
                <Textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                  placeholder="Enter policy details..."
                  rows={15}
                  className="bg-secondary/50 font-mono text-sm"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary-glow"
              onClick={handleUpdatePolicy}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
              <History className="w-5 h-5" />
              Change History
            </DialogTitle>
            <DialogDescription>
              {selectedPolicy?.title} - Version {policyHistory?.current_version || 1}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : policyHistory?.history?.length > 0 ? (
              <div className="space-y-4">
                {policyHistory.history.map((entry, index) => (
                  <div 
                    key={entry.id} 
                    className={cn(
                      "relative pl-6 pb-4",
                      index !== policyHistory.history.length - 1 && "border-l-2 border-border"
                    )}
                  >
                    {/* Timeline dot */}
                    <div className={cn(
                      "absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px]",
                      entry.version === policyHistory.current_version 
                        ? "bg-primary" 
                        : entry.version === 1 
                          ? "bg-green-500" 
                          : "bg-muted-foreground"
                    )} />
                    
                    <div className="bg-secondary/30 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            v{entry.version}
                          </Badge>
                          {entry.version === policyHistory.current_version && (
                            <Badge className="bg-primary/20 text-primary text-xs">Current</Badge>
                          )}
                          {entry.version === 1 && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">Original</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {entry.changed_at && new Date(entry.changed_at).toLocaleString()}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2">
                        Changed by: <span className="text-foreground">{entry.changed_by_name}</span>
                      </p>
                      
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="space-y-1">
                          {entry.changes.map((change, changeIdx) => (
                            <div key={changeIdx} className="text-sm">
                              {change.field === 'policy' ? (
                                <span className="text-green-400">{change.new}</span>
                              ) : (
                                <span>
                                  <span className="text-muted-foreground capitalize">{change.field}:</span>{' '}
                                  {change.old && (
                                    <span className="text-red-400 line-through mr-2">{change.old}</span>
                                  )}
                                  <span className="text-green-400">{change.new}</span>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <History className="w-12 h-12 mb-4 opacity-50" />
                <p>No change history available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog (Super Admin - PH Library) */}
      <Dialog open={showEditTemplateDialog} onOpenChange={setShowEditTemplateDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
              Edit PH Library Template
            </DialogTitle>
            <DialogDescription>
              Update this policy template in the Players Health library
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Template Title *</Label>
                <Input
                  value={templateForm.title}
                  onChange={(e) => setTemplateForm({...templateForm, title: e.target.value})}
                  placeholder="e.g., Concussion Protocol Policy"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={templateForm.category} 
                  onValueChange={(v) => setTemplateForm({...templateForm, category: v})}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {POLICY_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Input
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                  placeholder="Brief description of this policy template"
                  className="bg-secondary/50"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Applicable Organization Types</Label>
                <div className="flex flex-wrap gap-2">
                  {ORG_TYPE_FILTERS.filter(t => t.value !== 'all').map(type => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={templateForm.applicable_org_types.includes(type.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        const newTypes = templateForm.applicable_org_types.includes(type.value)
                          ? templateForm.applicable_org_types.filter(t => t !== type.value)
                          : [...templateForm.applicable_org_types, type.value];
                        setTemplateForm({...templateForm, applicable_org_types: newTypes});
                      }}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Leave empty for universal policies</p>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Template Content *</Label>
                <Textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                  placeholder="Enter policy template content..."
                  rows={12}
                  className="bg-secondary/50 font-mono text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditTemplateDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary-glow"
              onClick={handleUpdateTemplate}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
